import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {
  DEFAULT_GROUP_PIPELINE_VIEW,
  GROUP_PIPELINE_VIEW_KEY,
  type GroupPipelineViewConfig,
} from '@common/groupPipeline/viewConfig';

// The signed-in user's saved layout for the Group Pipeline page, falling back to the defaults.
export function useGroupPipelineView() {
  const utils = trpc.useUtils();
  const input = {viewKey: GROUP_PIPELINE_VIEW_KEY};
  const {data, isLoading} = trpc.internal.savedView.get.useQuery(input);
  const view = (data as GroupPipelineViewConfig | null | undefined) ?? DEFAULT_GROUP_PIPELINE_VIEW;

  const save = trpc.internal.savedView.save.useMutation({
    onMutate: async ({config}) => {
      await utils.internal.savedView.get.cancel(input);
      utils.internal.savedView.get.setData(input, config as Record<string, unknown>);
    },
    onError: createMutationErrorHandler('Could not save your view'),
    onSettled: () => utils.internal.savedView.get.invalidate(input),
  });

  const reset = trpc.internal.savedView.reset.useMutation({
    onMutate: () => utils.internal.savedView.get.setData(input, null),
    onError: createMutationErrorHandler('Could not reset your view'),
    onSettled: () => utils.internal.savedView.get.invalidate(input),
  });

  return {
    view,
    isLoading,
    isCustomized: Boolean(data),
    update: (changes: Partial<GroupPipelineViewConfig>) =>
      save.mutate({...input, config: {...view, ...changes}}),
    reset: () => reset.mutate(input),
  };
}
